import React, { useRef, useState, useEffect } from 'react';
import { brandImagesWorking, brandImagesDevelop } from '../../../data/home/home';

const VISIBLE_COUNT = 4;
const AUTO_SLIDE_INTERVAL = 2500;

// Reintroduce BrandsRow as a subcomponent with its own state for navigation and hover
const BrandsRow: React.FC<{ brandImages: string[] }> = ({ brandImages }) => {
  const [startIdx, setStartIdx] = useState(0);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [showButtons, setShowButtons] = useState(false);
  const [isSliding, setIsSliding] = useState(false);
  const [slideDirection, setSlideDirection] = useState<'left' | 'right'>('left');
  const autoSlideRef = useRef<NodeJS.Timeout | null>(null);
  const isHovered = showButtons;
  const total = brandImages.length;
  const touchStartX = useRef<number | null>(null);

  useEffect(() => {
    if (!isHovered) {
      autoSlideRef.current = setInterval(() => {
        triggerSlide('left');
      }, AUTO_SLIDE_INTERVAL);
    } else if (autoSlideRef.current) {
      clearInterval(autoSlideRef.current);
    }
    return () => {
      if (autoSlideRef.current) clearInterval(autoSlideRef.current);
    };
  }, [isHovered, total]);

  const scroll = (direction: 'left' | 'right') => {
    triggerSlide(direction);
  };

  const triggerSlide = (direction: 'left' | 'right') => {
    setSlideDirection(direction);
    setIsSliding(true);
    setTimeout(() => {
      setStartIdx((prev) => {
        if (direction === 'left') {
          return (prev + 1) % total;
        } else {
          return (prev - 1 + total) % total;
        }
      });
      setIsSliding(false);
    }, 400);
  };

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
    if (touchStartX.current !== null) {
      const diff = e.changedTouches[0].clientX - touchStartX.current;
      if (diff > 40) scroll('right');
      else if (diff < -40) scroll('left');
    }
    touchStartX.current = null;
  };

  const getTranslateX = () => {
    if (!isSliding) return '0%';
    return slideDirection === 'left' ? '-25%' : '25%';
  };

  return (
    <div
      className="relative group mb-6"
      onMouseEnter={() => setShowButtons(true)}
      onMouseLeave={() => { setShowButtons(false); setHoveredIndex(null); }}
    >
      {/* Scroll Buttons (hide on mobile) */}
      {showButtons && (
        <>
          <button
            className="hidden sm:flex absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/80 dark:bg-black/60 rounded-full p-2 shadow hover:bg-blue-100 dark:hover:bg-blue-900 transition-opacity opacity-80 hover:opacity-100"
            onClick={() => scroll('right')}
            aria-label="Scroll left"
            tabIndex={0}
          >
            <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M15 19l-7-7 7-7" /></svg>
          </button>
          <button
            className="hidden sm:flex absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/80 dark:bg-black/60 rounded-full p-2 shadow hover:bg-blue-100 dark:hover:bg-blue-900 transition-opacity opacity-80 hover:opacity-100"
            onClick={() => scroll('left')}
            aria-label="Scroll right"
            tabIndex={0}
          >
            <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M9 5l7 7-7 7" /></svg>
          </button>
        </>
      )}
      {/* Brand Logos */}
      <div
        className="relative h-[60px] xs:h-[80px] sm:h-[120px] md:h-[140px] w-full overflow-x-auto sm:overflow-hidden flex snap-x snap-mandatory"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <div
          className="flex w-full h-full"
          style={{
            width: `${VISIBLE_COUNT * 100 / VISIBLE_COUNT}%`,
            transform: `translateX(${getTranslateX()})`,
            transition: isSliding ? 'transform 0.4s cubic-bezier(0.4,0,0.2,1)' : 'none',
          }}
        >
          {Array.from({ length: VISIBLE_COUNT }, (_, i) => brandImages[(startIdx + i) % total]).map((src: string, idx: number) => (
            <div
              key={src + '-' + idx}
              className={`flex-1 flex flex-col items-center justify-center transition-transform duration-300 ease-in-out ${hoveredIndex === idx ? 'scale-110 z-10' : 'scale-100'} cursor-pointer snap-center`}
              style={{ minWidth: 0, minHeight: 40 }}
              onMouseEnter={() => setHoveredIndex(idx)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              <span className="block w-full max-w-[80px] xs:max-w-[100px] sm:max-w-[140px] md:max-w-[180px] h-8 xs:h-12 sm:h-16 md:h-24 mx-auto flex items-center justify-center dark:bg-white bg-transparent dark:border dark:border-white/60 dark:rounded-xl rounded-xl transition-all duration-300">
                <img
                  src={src}
                  alt={`Brand ${idx + 1}`}
                  className="w-full h-full object-contain transition-all duration-300 drop-shadow-lg"
                  draggable={false}
                  style={{ filter: hoveredIndex === idx ? 'none' : 'grayscale(100%)' }}
                />
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const BrandsSection: React.FC = () => {
  return (
    <section className="py-10 sm:py-14 md:py-16 bg-transparent dark:bg-black transition-colors duration-300 mt-8 sm:mt-12">
      <div className="container mx-auto px-2 sm:px-4">
        <BrandsRow brandImages={brandImagesWorking} />
        <BrandsRow brandImages={brandImagesDevelop} />
      </div>
    </section>
  );
};

export default BrandsSection; 